import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DateInput } from "./DateInput";
import { Select, type SelectOption } from "./Select";
import { findFlatLocation, locationSelectOptions, type FlatLocation } from "../lib/locationsFlat";
import { useLocationKinds } from "../lib/useLocationKinds";
import { LocationKindIcon } from "./LocationKindIcon";

/**
 * "Where does the piece go" block shared by the asset form, dismount and move
 * modals: required physical location over the flat tree, free-text note, and —
 * only when the chosen location's kind behaves as EXTERNAL_SERVICE — the mandatory expected
 * return date (contract §3: dismount/move to a service location).
 */
export function LocationDestinationFields({
  flat, locationId, onLocationChange, locationNote, onLocationNoteChange,
  expectedReturnAt, onExpectedReturnAtChange, label, disabled,
}: {
  flat: FlatLocation[] | null;
  locationId: string;
  onLocationChange: (id: string) => void;
  locationNote: string;
  onLocationNoteChange: (v: string) => void;
  expectedReturnAt: string;
  onExpectedReturnAtChange: (v: string) => void;
  label?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { iconOf } = useLocationKinds();
  const options = useMemo<SelectOption<string>[]>(
    () => locationSelectOptions(flat ?? [], { left: (e) => <LocationKindIcon icon={iconOf(e.kind)} /> }),
    [flat, iconOf],
  );
  const selected = findFlatLocation(flat ?? [], locationId);
  const isService = selected?.kindBehavior === "EXTERNAL_SERVICE";

  return (
    <>
      <label className="field">
        <span>{label ?? t("assets.field.location", "Fizička lokacija")} <span className="req-mark">*</span></span>
        <Select<string>
          value={locationId}
          options={options}
          onChange={onLocationChange}
          disabled={disabled || flat === null}
          placeholder={flat === null ? t("common.loading", "Učitavanje…") : t("assets.field.locationPlaceholder", "— izaberite lokaciju —")}
          ariaLabel={label ?? t("assets.field.location", "Fizička lokacija")}
        />
        {selected && <span className="muted" style={{ fontSize: 12 }}>{selected.pathLabel}</span>}
      </label>
      <label className="field">
        <span>{t("assets.field.locationNote", "Napomena lokacije")}</span>
        <input
          className="input"
          value={locationNote}
          onChange={(e) => onLocationNoteChange(e.target.value)}
          maxLength={200}
          disabled={disabled}
          placeholder={t("assets.field.locationNotePlaceholder", "npr. na polici pored vrata")}
        />
      </label>
      {isService && (
        <label className="field">
          <span>{t("assets.field.expectedReturnAt", "Očekivani povratak sa servisa")} <span className="req-mark">*</span></span>
          <DateInput
            value={expectedReturnAt}
            onChange={onExpectedReturnAtChange}
            required
            disabled={disabled}
          />
        </label>
      )}
    </>
  );
}

/** True when the chosen location is a service and no return date was entered. */
export function missingReturnDate(flat: FlatLocation[] | null, locationId: string, expectedReturnAt: string): boolean {
  const sel = findFlatLocation(flat ?? [], locationId);
  return sel?.kindBehavior === "EXTERNAL_SERVICE" && !expectedReturnAt;
}

/** Return date is only meaningful for a service location; drop it otherwise. */
export function returnDateFor(flat: FlatLocation[] | null, locationId: string, expectedReturnAt: string): string | null {
  const sel = findFlatLocation(flat ?? [], locationId);
  return sel?.kindBehavior === "EXTERNAL_SERVICE" && expectedReturnAt ? expectedReturnAt : null;
}
